import { useQuery, useMutation, useQueryClient } from 'react-query';

import api from '@src/utils/api';
import { MEDIA, BRANDING_PRESETS, BRANDING_BURNS } from '@src/constants/endpoints';

export const BRANDING_PRESETS_QUERY_KEY = 'brandingPresets';
export const BRANDING_BURNS_QUERY_KEY = 'brandingBurns';

/** Matches CAPTION_POLLING_INTERVAL_MS — a burn takes about as long as a caption burn-in. */
export const BRANDING_POLLING_INTERVAL_MS = 3000;

/**
 * True while any burn in the list is still being produced.
 *
 * Drives the polling interval: the burn happens in a Lambda and reports back out-of-band,
 * so the only way the UI learns it finished is by asking again. Polling stops as soon as
 * nothing is running, rather than hammering a settled list.
 */
export const isBrandingDataProcessing = (data) => {
  const items = Array.isArray(data) ? data : [];
  return items.some((b) => b.status === 'pending' || b.status === 'processing');
};

/**
 * Retry policy for the branding queries.
 *
 * Every branding route asserts VIDEO_BRANDING_ENABLED, so a user without it gets 403 on the first
 * call and on every retry alike. The default three retries turned that into eight wasted requests
 * and, worse, kept `isError` false through the whole backoff — leaving the editor on screen for
 * seconds after the answer was already known, long enough to click Burn. Everything else still
 * retries: a 403 is a decision, a 500 is a bad moment.
 */
export const retryUnlessForbidden = (failureCount, error) =>
  (error?.response?.status === 403 ? false : failureCount < 3);

// ─── Presets (per organization) ───────────────────────────────────────────────

const fetchOrgPresets = () => api.get(BRANDING_PRESETS);

export const useOrgPresets = (options = {}) =>
  useQuery([BRANDING_PRESETS_QUERY_KEY], fetchOrgPresets, {
    staleTime: 30000,
    retry: retryUnlessForbidden,
    ...options
  });

/**
 * Creates or updates a preset depending on whether an `id` is supplied, so the caller can
 * use one mutation for both the "Save as preset" and "Save changes" paths.
 */
const savePreset = ({ id, payload }) =>
  id ? api.put(`${BRANDING_PRESETS}/${id}`, payload) : api.post(BRANDING_PRESETS, payload);

export const useSavePreset = () => {
  const queryClient = useQueryClient();
  return useMutation(savePreset, {
    onSuccess: () => {
      queryClient.invalidateQueries([BRANDING_PRESETS_QUERY_KEY]);
    }
  });
};

const deletePreset = (id) => api.delete(`${BRANDING_PRESETS}/${id}`);

export const useDeletePreset = () => {
  const queryClient = useQueryClient();
  return useMutation(deletePreset, {
    onSuccess: () => {
      queryClient.invalidateQueries([BRANDING_PRESETS_QUERY_KEY]);
    }
  });
};

// ─── Burns (per media) ────────────────────────────────────────────────────────

const fetchMediaBrandingBurns = ({ queryKey }) => {
  const [, mediaId] = queryKey;
  return api.get(`${MEDIA}/${mediaId}/branding-burns`);
};

/**
 * Lists the branding burns of a media, polling while one is in flight.
 *
 * Each entry doubles as a media "version" (kind: 'branding', label: 'branded'), matching the
 * shape the captions versions endpoint returns — which is what lets VpMediaSubTable merge
 * both kinds into one list instead of branching per type.
 */
export const useMediaBrandingBurns = (mediaId, options = {}) =>
  useQuery([BRANDING_BURNS_QUERY_KEY, mediaId], fetchMediaBrandingBurns, {
    enabled: !!mediaId,
    staleTime: 30000,
    retry: retryUnlessForbidden,
    refetchInterval: (data) => (isBrandingDataProcessing(data) ? BRANDING_POLLING_INTERVAL_MS : false),
    ...options
  });

/** Starts a burn from either a saved preset (`presetId`) or an ad-hoc `config`. */
const startBrandingBurn = ({ mediaId, presetId, config }) =>
  api.post(`${MEDIA}/${mediaId}/branding-burns`, presetId ? { presetId } : { config });

export const useStartBrandingBurn = (mediaId) => {
  const queryClient = useQueryClient();
  return useMutation(
    (variables) => startBrandingBurn({ mediaId, ...variables }),
    {
      onSuccess: () => {
        // The new burn arrives as `pending`; invalidating both keys makes the versions list
        // pick it up and start polling without waiting for staleTime to elapse.
        queryClient.invalidateQueries([BRANDING_BURNS_QUERY_KEY, mediaId]);
        queryClient.invalidateQueries(['captionVersions', mediaId]);
      }
    }
  );
};

const deleteBrandingBurn = (burnId) => api.delete(`${BRANDING_BURNS}/${burnId}`);

export const useDeleteBrandingBurn = (mediaId) => {
  const queryClient = useQueryClient();
  return useMutation(deleteBrandingBurn, {
    onSuccess: () => {
      queryClient.invalidateQueries([BRANDING_BURNS_QUERY_KEY, mediaId]);
      queryClient.invalidateQueries(['captionVersions', mediaId]);
    }
  });
};



// WEBPACK FOOTER //
// ./src/hooks/react-query/useBrandingData.js